import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDomainGlossaryOptional } from "@/hooks/use-domain-glossary-tips";
import domainGlossary from "@/data/domainGlossary.json";

type GlossaryEntry = (typeof domainGlossary)["entries"][string];

function getEntry(termId: string): GlossaryEntry | undefined {
  return domainGlossary.entries[termId as keyof typeof domainGlossary.entries] as GlossaryEntry | undefined;
}

type DomainTermInfoProps = {
  /** Key in `src/data/domainGlossary.json` → `entries` */
  termId: string;
  className?: string;
  /** Smaller hit target in dense tables */
  size?: "sm" | "md";
};

/**
 * Renders a hover/focus help icon with glossary text. Hidden when the user turns off
 * “Domain term hints” in Settings (see `DomainGlossaryProvider`).
 */
export function DomainTermInfo({ termId, className, size = "sm" }: DomainTermInfoProps) {
  const { showDomainTips } = useDomainGlossaryOptional();
  const entry = getEntry(termId);

  if (!showDomainTips || !entry) {
    return null;
  }

  const iconSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-full text-muted-foreground",
            "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "shrink-0 -translate-y-px",
            className
          )}
          aria-label={`What is: ${entry.label}`}
        >
          <HelpCircle className={iconSize} strokeWidth={2} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        className="max-w-xs space-y-1.5 border bg-popover px-3 py-2 text-left text-sm shadow-md"
        side="top"
      >
        <p className="font-semibold leading-tight text-foreground">{entry.label}</p>
        <p className="text-xs font-normal leading-snug text-muted-foreground">{entry.description}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function hasGlossaryEntry(termId: string): boolean {
  return Boolean(getEntry(termId));
}
